/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import {
	queryOptions,
	useQuery,
} from '@tanstack/react-query';

// Types
import { TParamsGetApi } from '@global-types/index';

// Lib
import { apiClient } from '@lib/api-client';

// Constants
import { queryKeys } from '@config/constants/query-keys';
import { TOffset } from '@global-types/api/api';
import { QueryConfig } from '@lib/react-query';
import { formatTwoDecimals } from '@utils/formats';
import { formatDate } from '@utils/functions';
import { getUnitLabel } from '@utils/units';
import Decimal from 'decimal.js';

/* -------------------------------------------------------------------------- */
/*                                     Api                                    */
/* -------------------------------------------------------------------------- */
export const getOffsets = (
	params: Partial<TParamsGetApi>
): Promise<TApiResponseGetOffsets> => {
	return apiClient.get('/offsets', {
		params,
	});
};

/* -------------------------------------------------------------------------- */
/*                                    Type                                    */
/* -------------------------------------------------------------------------- */

type TApiResponseGetOffsets = {
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
	rows: TOffset[];
};

type TUseGetOffsetsOptions = {
	params: Partial<TParamsGetApi>;
	queryConfig?: QueryConfig<typeof getOffsets>;
};

/* -------------------------------------------------------------------------- */
/*                                    Helper                                  */
/* -------------------------------------------------------------------------- */
export const getOffsetsQueryOptions = (
	params: Partial<TParamsGetApi> = {}
) => {
	return queryOptions({
		queryKey: params
			? [queryKeys.app.offsets.all.key, { params }]
			: [queryKeys.app.offsets.all.key],
		queryFn: () => getOffsets(params),
	});
};

/* -------------------------------------------------------------------------- */
/*                                     Hook                                   */
/* -------------------------------------------------------------------------- */
export const useGetOffsets = ({
	params,
	queryConfig,
}: TUseGetOffsetsOptions) => {
	const { data, ...rest } = useQuery({
		...getOffsetsQueryOptions(params),
		...queryConfig,
	});

	const updateList = (offsets: TOffset[]) => {
		return offsets && Array.isArray(offsets)
			? offsets.reduce((acc: any, currentItem: any) => {
					const quantityOffset = new Decimal(
						currentItem.quantity_gco2eq
					);
					const quantity = Decimal.div(
						quantityOffset.toNumber(),
						1000000
					);

					const modifiedItem = {
						...currentItem,
						contributionDate: formatDate(
							currentItem.createdAt,
							'DD/MM/YYYY'
						),
						priceTTC: currentItem?.amount_euro || 0,
						quantityParsed: `${formatTwoDecimals(
							quantity.toNumber()
						)} ${getUnitLabel('tco2e')}`,
					};
					acc.push(modifiedItem);

					return acc;
				}, [])
			: [];
	};

	return {
		...rest,
		data: data ? updateList(data?.rows) : [],
	};
};
